import {openDeleteDialog, openDialog, showNotification} from 'platform/components';
import {Box, Space} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isArray} from 'ramda-adjunct';

import {useDeleteTeamMutation} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  handleApiError,
  usePermissions,
} from '@dms/shared';

import {ApiError, composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {isTeamDeleteBlockedByConditions} from '../utils/isTeamDeleteBlockedByConditions';
import {TeamDeleteBlockedDialog} from './TeamDeleteBlockedDialog';

const PREDEFINED_COLUMNS = ['name'] as const;

export function TeamOverview() {
  const navigate = useNavigate();
  const [deleteTeam] = useDeleteTeamMutation();

  const [canCreateTeam, canDeleteTeam] = usePermissions({
    permissionKeys: ['teamCreate', 'teamDelete'],
  });

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    refreshData,
  }) => {
    const id = isArray(rowId) ? rowId[0] : rowId;

    match(actionKey)
      .with('edit', 'detail', () => {
        navigate(composePath(settingsRoutes.teamManagementDetail, {params: {id}}));
      })
      .with('delete', () => {
        if (!canDeleteTeam) {
          showNotification.error(i18n.t('general.labels.noPermission'));
          return;
        }

        openDeleteDialog({
          onConfirm: () =>
            deleteTeam({id})
              .unwrap()
              .then(() => {
                showNotification.success(i18n.t('general.notifications.successfullyDeleted'));
                refreshData();
              })
              .catch((error: ApiError) => {
                if (!isTeamDeleteBlockedByConditions(error)) {
                  handleApiError(error);
                  return;
                }

                openDialog(
                  <TeamDeleteBlockedDialog
                    teamId={id}
                    onDeleted={refreshData}
                    data-testid={testIds.settings.teamManagement('deleteBlocked')}
                  />,
                  {
                    title: i18n.t('entity.team.labels.deleteBlockedTitle'),
                    size: 'small',
                    scrollBehavior: 'outside',
                  }
                );
              }),
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <SettingsTemplate
      header={{
        title: i18n.t('page.settings.labels.teamManagement'),
        actions: [
          {
            type: 'button',
            title: i18n.t('page.settings.labels.addTeam'),
            onClick: () => navigate(settingsRoutes.teamManagementAddTeam),
            'data-testid': testIds.settings.teamManagement('newTeamButton'),
            isDisabled: !canCreateTeam,
          },
        ],
      }}
      data-testid={testIds.settings.teamManagement('page')}
    >
      <Box width={177}>{i18n.t('page.settings.labels.teamManagementDescription')}</Box>

      <Space vertical={8} />

      <Box>
        <DmsDataGrid
          gridCode="team"
          actionCallback={actionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
          data-testid={testIds.settings.teamManagement('teamDG')}
          autoHeight
        />
      </Box>
    </SettingsTemplate>
  );
}
